import { UpdatedFiltersType } from 'model/Article';
import { articlesActions } from '@/store/articles';
import { useMemo, useState } from 'react';
import useHelpers from 'hooks/helpers';
import { useDispatch } from 'react-redux';
import useWindowDimensions from 'hooks/useWindowDimensions';

const useSharedFiltersLogic = (initialFiltersState) => {
  const [filters, setFilters] = useState(initialFiltersState);
  const { updateUrlParams } = useHelpers();
  const dispatch = useDispatch();
  const deviceType = useWindowDimensions();

  const getFiltersFromURL = (applicableFilters: Array<string>) => {
    const params = new URLSearchParams(window.location.search);
    const result: Array<{ name: string; values: Array<string> }> = [];

    for (const [key, value] of params.entries()) {
      const values = value.split(',').map(decodeURIComponent);
      if (applicableFilters.includes(key)) {
        result.push({ name: key, values });
      }
    }

    return result;
  };

  const allOptions = useMemo(() => {
    return filters
      .map((filter) => filter.options?.filter((option) => option.aggregateCount !== null))
      .flat()
      .filter((option) => option);
  }, [filters]);

  const appliedFilters = useMemo(() => {
    const targetFilter: Array<UpdatedFiltersType> = filters?.filter((filter) => {
      return filter.options.some((option) =>
        deviceType === 'mobile' ? option.selected : option.applied
      );
    });

    return targetFilter?.map((filter) => ({
      name: filter.name,
      values: filter.options
        .filter((option) => (deviceType === 'mobile' ? option.selected : option.applied))
        .map((option) => option.text),
    }));
  }, [filters, deviceType]);

  const appliedFilterOptions = useMemo(() => {
    return (
      appliedFilters
        ?.map((filter) => filter.values.map((value) => ({ displayName: value })))
        .flat() ?? []
    );
  }, [appliedFilters]);

  const selectedFilterOptions = useMemo(() => {
    return filters?.map((filter) => ({
      id: filter.id,
      selectedOptions: filter.options.filter((option) => option.selected),
    }));
  }, [filters]);

  const selectFilter = (option, selectedFilter) => {
    const updatedFilters: Array<UpdatedFiltersType> = filters?.map((filter) => {
      return {
        ...filter,
        options: filter.options.map((filterOption) => {
          return {
            ...filterOption,
            selected:
              selectedFilter === filter.name && filterOption.text === option.text
                ? !filterOption.selected
                : filterOption.selected,
          };
        }),
      };
    });
    setFilters(updatedFilters);
  };

  const filterApply = () => {
    const updatedFilters: Array<UpdatedFiltersType> = filters?.map((filter) => {
      return {
        ...filter,
        options: filter.options.map((filterOption) => {
          return {
            ...filterOption,
            applied: !!filterOption.selected,
          };
        }),
      };
    });

    setFilters(updatedFilters);

    const updatedAppliedFilters = updatedFilters.map((filter) => ({
      name: filter.name,
      values: filter.options.filter((option) => option.selected).map((option) => option.text),
    }));

    dispatch(articlesActions.setAppliedFilters(updatedAppliedFilters));
    updatedAppliedFilters
      .filter((filter) => filter.values.length)
      .map((filter) => {
        updateUrlParams([
          {
            name: filter.name,
            value: filter.values,
          },
        ]);
      });
  };

  const onResetUnAppliedOptions = () => {
    const updated = filters.map((filter) => ({
      ...filter,
      options: filter.options.map((option) => ({
        ...option,
        selected: option.applied,
      })),
    }));
    setFilters(updated);
  };

  const onClearFilters = (option: UpdatedFiltersType, actionType: string) => {
    const updatedFilters: Array<UpdatedFiltersType> = filters?.map((filter) => {
      return {
        ...filter,
        options: filter.options.map((filterOption) => {
          if (actionType === 'singleOption') {
            const condition =
              option.displayName === filterOption.text ? false : filterOption.selected;
            return {
              ...filterOption,
              selected: condition,
              applied: condition,
            };
          } else if (actionType === 'multipleOptions') {
            const condition = option.options.find((item) => item.text === filterOption.text)
              ? false
              : filterOption.selected;
            return {
              ...filterOption,
              selected: condition,
              applied: condition,
            };
          } else {
            return {
              ...filterOption,
              selected: false,
              applied: false,
            };
          }
        }),
      };
    });
    setFilters(updatedFilters);

    const filterWithAppliedOptions = updatedFilters?.filter((filter) => {
      return filter.options.some((option) => option.applied);
    });
    const result = filterWithAppliedOptions?.map((filter) => ({
      name: filter.name,
      values: filter.options.filter((option) => option.applied).map((option) => option.text),
    }));
    dispatch(articlesActions.setAppliedFilters(result));

    if (!result.length) {
      updatedFilters.map((filter) => updateUrlParams([{ name: filter.name, value: '' }]));
    } else {
      const urlFilters = updateUrlParams([], 'take');

      const resultFilterNames = new Set(result?.map((r) => r.name));
      result?.forEach((option) => {
        updateUrlParams([{ name: option.name, value: option.values || '' }]);
      });

      Object.keys(urlFilters).forEach((key) => {
        if (!resultFilterNames.has(key)) {
          updateUrlParams([{ name: key, value: '' }]);
        }
      });
    }
  };

  return {
    selectFilter,
    filterApply,
    onResetUnAppliedOptions,
    onClearFilters,
    getFiltersFromURL,
    filters,
    setFilters,
    allOptions,
    appliedFilters,
    appliedFilterOptions,
    selectedFilterOptions,
  };
};

export default useSharedFiltersLogic;
